import * as React from 'react';
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { NavLink, RouteComponentProps, withRouter, } from "react-router-dom";
import { Badge, Nav, NavbarBrand, NavItem } from "reactstrap";
import { IconName } from "@fortawesome/fontawesome-svg-core";

import { LocalStorageGet, LocalStorageSet } from "@app/Helpers/LocalStorageHelper";
import { appStore } from "@app/Stores/AppStore";
import { stationsStore } from "@app/Stores/StationsStore";
import { devInfoStore } from "@app/Stores/DevInfoStore";
import { unsavedChangesStore } from "@app/Stores/UnsavedChangesStore";

import { AzureAuth } from "../AzureAuth";
import ThemeSwitcher from "../ThemeSwitcher";
import { Icon } from "../Icon";
import { HoverMenu } from "../HoverMenu/HoverMenu";
import { modalService } from "../Modal";
import { ImpersonationDialog } from "../Dialogs/ImpersonationDialog";
import { ToolTipItem } from "../ToolTipItem";
import DropdownNavItem from "./Components/DropdownNavItem";
import { ConnectionStatus } from "./Components/ConnectionStatus";
import "./_nav-sidebar.scss";

type NavSidebarProps = RouteComponentProps & {};

const SIDEBAR_COLLAPSED_KEY = 'sidebar.collapsed';
const USER_NAME_TOOLTIP_TARGET_ID = 'nav-sidebar-user-name';

class NavSidebar extends React.Component<NavSidebarProps, {}> {
    @observable private _isCollapsed: boolean = LocalStorageGet<boolean>(SIDEBAR_COLLAPSED_KEY) || false;

    render() {
        return (
            <div className={`sidebar nav-sidebar ${this._isCollapsed ? 'collapsed' : ''}`}>
                <div className="position-relative mb-4">
                    <div className="d-flex nav-header">
                        <NavbarBrand href={AzureAuth.defaultRoute} onClick={(e) => this._onNavClick(e, AzureAuth.defaultRoute)} className="nav-sidebar-brand" title={devInfoStore.appName}>{this._isCollapsed ? devInfoStore.appCode : devInfoStore.appName}</NavbarBrand>
                    </div>
                    <div className="nav-sidebar-version">
                        <span>{devInfoStore.appVersion}</span>
                        <span className="ml-1" style={{ color: devInfoStore.envColor }}>{this._isCollapsed ? devInfoStore.appEnvironment.slice(0, 3) : devInfoStore.appEnvironment}</span>
                    </div>
                </div>

                <div className="d-flex flex-column justify-content-between h-100">
                    <Nav vertical pills>
                        {this._renderNavLinks()}
                    </Nav>
                    <div className="nav-sidebar-user">
                        <div className="user-avatar-wrap">
                            <HoverMenu
                                iconContent={this._getUserName}
                                hoverText={appStore.currentUserName}
                                items={[...this._hoverMenuItems]}
                                className={`user-avatar ${this._isCollapsed ? 'user-avatar-visible' : ''}`}
                            />
                            {this._isCollapsed && AzureAuth.isImpersonatingPermissions && (
                                <Icon
                                    name="eye-slash"
                                    title="Exit impersonation"
                                    className="impersonation-badge impersonation-badge--floating"
                                    onClick={this._exitImpersonation}
                                />
                            )}
                        </div>
                        <ConnectionStatus isCollapsed />

                        <div className="user-expanded">
                            <ConnectionStatus />
                            <span id={USER_NAME_TOOLTIP_TARGET_ID} className="user-name">
                                {appStore.currentUserName}
                            </span>
                            <ToolTipItem targetId={USER_NAME_TOOLTIP_TARGET_ID} placement="top">
                                {this._userEmail}
                            </ToolTipItem>
                            {AzureAuth.isImpersonatingPermissions && (
                                <Icon
                                    name="eye-slash"
                                    title="Exit impersonation"
                                    className="impersonation-badge impersonation-badge--inline"
                                    onClick={this._exitImpersonation}
                                />
                            )}
                            <HoverMenu
                                iconContent={<Icon name={'ellipsis-h'} />}
                                items={[...this._hoverMenuItems]}
                                className={'user-settings'}
                            />
                        </div>
                    </div>

                </div>
                <div className={'toggle'} onClick={this._handleToggle} title={this._isCollapsed ? 'Expand' : 'Collapse'}>
                    <Icon name={'arrow-left'} className={'icon'} />
                </div>
            </div>
        );
    }

    private _renderNavLinks() {
        const isDcc = AzureAuth.isDcc;

        return (
            <>
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <NavItem>
                        <NavLink exact to="/" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/")}>
                            <Icon name="cogs" title={'Jobs'} />
                            <span className="nav-sidebar-title">Jobs</span>
                        </NavLink>
                    </NavItem>
                )}
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <NavItem>
                        <NavLink exact to="/health-board" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/health-board")}>
                            <Icon name="heart-pulse" title={'Health Board'} />
                            <span className="nav-sidebar-title">Health Board</span>
                        </NavLink>
                    </NavItem>
                )}
                {AzureAuth.canSeeFlights && (
                    <DropdownNavItem
                        title="Flights"
                        icon="plane"
                        items={[
                            { title: "Gantt", to: "/flight-schedule/gantt", icon: 'stream', onClick: (e) => this._onNavClick(e, "/flight-schedule/gantt") },
                            { title: "List", to: "/flight-schedule/list", icon: 'table', onClick: (e) => this._onNavClick(e, "/flight-schedule/list") }
                        ]}
                    />
                )}
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <NavItem>
                        <NavLink exact to="/email-viewer" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/email-viewer")}>
                            <Icon name="envelope" title={'Email Viewer'} />
                            <span className="nav-sidebar-title">Email Viewer</span>
                        </NavLink>
                    </NavItem>
                )}
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <NavItem>
                        <NavLink exact to="/configs" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/configs")}>
                            <Icon name="cog" title={'Configs'} />
                            <span className="nav-sidebar-title">Configs</span>
                        </NavLink>
                    </NavItem>
                )}
                {AzureAuth.canSeeStations && (
                    <NavItem>
                        <NavLink to="/stations" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/stations")}>
                            <Icon name="plane-departure" title={'Stations'} />
                            <span className="nav-sidebar-title">Stations</span>
                            {stationsStore.stationsMissingDataCount > 0 && (
                                <Badge color="danger" pill className="nav-sidebar-badge"
                                    title={this._missingDataTitle()}>
                                    {stationsStore.stationsMissingDataCount}
                                </Badge>
                            )}
                        </NavLink>
                    </NavItem>
                )}
                {AzureAuth.canSeeAircrafts && (
                    <NavItem>
                        <NavLink to="/aircrafts" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/aircrafts")}>
                            <Icon name="plane-up" title={'Aircraft'} />
                            <span className="nav-sidebar-title">Aircraft</span>
                        </NavLink>
                    </NavItem>
                )}
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <DropdownNavItem
                        title="PeopleAssist"
                        icon="people-group"
                        items={[
                            { title: "History", to: "/people-assist/history", icon: 'clock-rotate-left', onClick: (e) => this._onNavClick(e, "/people-assist/history") },
                            { title: "Viewer", to: "/people-assist/viewer", icon: 'satellite-dish', onClick: (e) => this._onNavClick(e, "/people-assist/viewer") },
                            { title: "Illness", to: "/people-assist/illness", icon: 'notes-medical', onClick: (e) => this._onNavClick(e, "/people-assist/illness") }
                        ]}
                    />
                )}
                {isDcc && AzureAuth.canSeeGeneralAdmin && (
                    <NavItem>
                        <NavLink exact to="/permissions-tool" className="nav-link" activeClassName="active" onClick={(e) => this._onNavClick(e, "/permissions-tool")}>
                            <Icon name="user-shield" title={'Permissions Tool'} />
                            <span className="nav-sidebar-title">Permissions Tool</span>
                        </NavLink>
                    </NavItem>
                )}
            </>
        );
    }

    private async _onNavClick(e: React.MouseEvent, path: string) {
        e.preventDefault();

        const canContinue = await unsavedChangesStore.showConfirmation();
        if (!canContinue) return;

        unsavedChangesStore.resetAll();
        this.props.history.push(path);
    }

    /** One line per gap kind the user can act on, so a single-permission user sees a single line. */
    private _missingDataTitle(): string {
        const lines: string[] = [];
        if (stationsStore.stationsMissingPantryCount > 0) {
            lines.push(`${stationsStore.stationsMissingPantryCount} station(s) missing a pantry for future destinations`);
        }
        if (stationsStore.stationsMissingWaterQuantityCount > 0) {
            lines.push(`${stationsStore.stationsMissingWaterQuantityCount} station(s) missing water quantity levels for future flights`);
        }
        return lines.join('\n');
    }

    @action.bound
    private _onLogoutClick() {
        AzureAuth.logOut();
    }

    private _exitImpersonation = () => {
        appStore.clearPermissionImpersonation();
        location.reload();
    };

    private _openImpersonationDialog = () => {
        void modalService.show(ImpersonationDialog);
    };

    @computed
    private get _userEmail(): string {
        return appStore.currentUser?.preferred_username || appStore.currentUser?.unique_name || 'Unknown email';
    }

    @action.bound
    private _handleToggle(event: React.MouseEvent<HTMLDivElement>) {
        event.stopPropagation();
        LocalStorageSet<boolean>(SIDEBAR_COLLAPSED_KEY, !this._isCollapsed);
        this._isCollapsed = !this._isCollapsed;
    }

    @computed
    private get _getUserName() {
        const source = appStore.currentUserName;
        const parts = source.trim().split(/\s+/);

        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }

        return parts[0]?.slice(0, 2).toUpperCase() || '';
    };

    @computed
    private get _hoverMenuItems() {
        const items = [];
        items.push({ type: 'theme', content: <ThemeSwitcher title={'Switch theme'} /> });
        if (AzureAuth.canImpersonatePermissions) {
            items.push({
                type: 'impersonate',
                content: (
                    <>
                        <Icon name="user-secret" title="Impersonate with active set" className="mr-2" />
                        <div>Impersonation</div>
                    </>
                ),
                onClick: this._openImpersonationDialog
            });
        }
        if (AzureAuth.isImpersonatingPermissions) {
            items.push({ type: 'exit-impersonation', icon: "eye-slash" as IconName, title: "Exit impersonation", onClick: this._exitImpersonation });
        }
        items.push({ type: 'logout', icon: "right-from-bracket" as IconName, title: "Log out", onClick: this._onLogoutClick });
        return items;
    }
}

export default withRouter(observer(NavSidebar));